import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Pagination from "@/components/Pagination";
import {
  ShieldWarning, ClockCounterClockwise, MagnifyingGlass, Calendar,
  DownloadSimple, User,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const ACTION_BADGE = {
  "auth.login_success": { label: "Login", cls: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  "auth.login_failed":  { label: "Login Failed", cls: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
  "auth.logout":        { label: "Logout", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  "po.created":         { label: "PO Created", cls: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
  "po.updated":         { label: "PO Updated", cls: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  "po.deleted":         { label: "PO Deleted", cls: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
  "invoice.uploaded":   { label: "Invoice Uploaded", cls: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
  "invoice.approved":   { label: "Invoice Approved", cls: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  "invoice.rejected":   { label: "Invoice Rejected", cls: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
  "invoice.deleted":    { label: "Invoice Deleted", cls: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
  "transfer.created":   { label: "Transfer Created", cls: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
  "transfer.approved":  { label: "Transfer Approved", cls: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  "transfer.rejected":  { label: "Transfer Rejected", cls: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
  "transfer.deleted":   { label: "Transfer Deleted", cls: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
  "boq.created":        { label: "BOQ Created", cls: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
  "boq.updated":        { label: "BOQ Updated", cls: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  "boq.deleted":        { label: "BOQ Deleted", cls: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
  "boq.imported":       { label: "BOQ Imported", cls: "bg-[#E8F0FE] text-[#1967D2] border-[#AECBFA]" },
};

export default function Activity() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("all");
  const [entityType, setEntityType] = useState("all");
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const buildParams = (targetPage) => {
    const params = { page: targetPage, page_size: pageSize };
    if (action !== "all") params.action = action;
    if (entityType !== "all") params.entity_type = entityType;
    if (search) params.search = search;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    return params;
  };

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/audit-logs", { params: buildParams(targetPage) });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = async () => {
    try {
      const resp = await api.get("/audit-logs.csv", { params: buildParams(1), responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audit-logs/actions");
        setActions(data.actions || []);
      } catch (e) { /* ignore — filter dropdown just won't populate */ }
    })();
  }, []);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, entityType, fromDate, toDate]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  if (user && user.role !== "admin") {
    return (
      <div className="p-10 text-center">
        <ShieldWarning size={48} className="mx-auto text-neutral-400 mb-3" />
        <h2 className="font-display text-2xl font-bold mb-2">Admin Only</h2>
        <p className="text-sm text-neutral-500">Activity log is available to admins only.</p>
      </div>
    );
  }

  return (
    <div className="p-8 sm:p-10 max-w-[1600px] mx-auto" data-testid="activity-page">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">Compliance</div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <ClockCounterClockwise size={32} className="text-primary" weight="fill" />
            Activity Log
          </h1>
          <p className="text-sm text-neutral-500 mt-2">
            {total} recorded event{total === 1 ? "" : "s"} — who did what and when across all projects.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-sm h-11 px-5 gap-2"
          onClick={downloadCsv}
          data-testid="activity-export-csv"
        >
          <DownloadSimple size={16} weight="bold" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Search</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Actor email / PO # / invoice # / action…"
              className="rounded-sm pl-9 h-10"
              data-testid="activity-search"
            />
          </div>
        </div>
        <div className="w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Action</label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="rounded-sm h-10" data-testid="activity-action-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              {actions.map((a) => (
                <SelectItem key={a} value={a}>{ACTION_BADGE[a]?.label || a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-[160px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Entity</label>
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="rounded-sm h-10" data-testid="activity-entity-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="po">Purchase Order</SelectItem>
              <SelectItem value="invoice">Invoice</SelectItem>
              <SelectItem value="transfer">Transfer</SelectItem>
              <SelectItem value="boq">BOQ</SelectItem>
              <SelectItem value="user">User (auth)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
            <Calendar size={11} /> From
          </label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            max={toDate || undefined}
            className="rounded-sm h-10 tabular-nums w-[160px]"
            data-testid="activity-from-date"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1">
            <Calendar size={11} /> To
          </label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            min={fromDate || undefined}
            className="rounded-sm h-10 tabular-nums w-[160px]"
            data-testid="activity-to-date"
          />
        </div>
        {(search || action !== "all" || entityType !== "all" || fromDate || toDate) && (
          <Button
            variant="ghost"
            className="rounded-sm h-10 text-neutral-500 hover:text-neutral-900"
            onClick={() => { setSearch(""); setAction("all"); setEntityType("all"); setFromDate(""); setToDate(""); }}
            data-testid="activity-clear-filters"
          >
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600">
              <tr>
                <th className="text-left px-6 py-3 font-medium text-xs uppercase tracking-wider">When</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Who</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Action</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Entity</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12 text-neutral-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-16 text-neutral-400">No events match these filters.</td></tr>
              ) : (
                rows.map((r, idx) => {
                  const badge = ACTION_BADGE[r.action] || { label: r.action, cls: "bg-neutral-100 text-neutral-700 border-neutral-200" };
                  const meta = r.metadata || {};
                  const detail =
                    r.entity_type === "po"       ? `${meta.vendor ? meta.vendor + " · " : ""}₹${(meta.amount || 0).toLocaleString("en-IN")}` :
                    r.entity_type === "invoice"  ? `PO ${meta.po_number || "—"}${meta.notes ? " · " + meta.notes.slice(0, 60) : ""}` :
                    r.entity_type === "transfer" ? `${meta.category || ""} ${meta.quantity || ""}${meta.unit || ""}${meta.notes ? " · " + meta.notes.slice(0, 60) : ""}` :
                    r.entity_type === "boq"      ? (
                      r.action === "boq.imported"
                        ? `${meta.inserted || 0} rows · ${meta.sheet || ""}${meta.replace ? " · REPLACED" : ""}`
                        : `${meta.total_qty ? `Qty ${meta.total_qty}` : ""}${meta.changed?.length ? ` · fields: ${meta.changed.join(", ")}` : ""}`
                    ) :
                    "";
                  return (
                    <tr key={r.id} className={`border-t border-neutral-100 hover:bg-neutral-50 ${idx % 2 ? "bg-neutral-50/40" : ""}`} data-testid={`activity-row-${r.id}`}>
                      <td className="px-6 py-3 text-neutral-500 tabular-nums whitespace-nowrap text-xs">
                        {r.timestamp ? new Date(r.timestamp).toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-sm bg-neutral-900 text-white flex items-center justify-center text-[10px] font-bold">
                            {(r.actor_name || r.actor_email || "?").slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-neutral-900 truncate">{r.actor_name || <span className="text-neutral-400">—</span>}</div>
                            <div className="text-[10px] text-neutral-500 truncate">{r.actor_email || ""}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-neutral-700 text-xs">
                        <div className="font-mono-num">{r.entity_label || r.entity_id || "—"}</div>
                        <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{r.entity_type || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-neutral-600 text-xs truncate max-w-md">{detail}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={(n) => load(n)}
          testIdPrefix="activity"
        />
      </div>
    </div>
  );
}
